// Directus SDK client setup for Quasar + Pinia + TypeScript

import { authentication, createDirectus, rest } from "@directus/sdk";
import type { DeviceModel, VirtualDeviceConfiguration } from "@irriga-mais/common";
import type { PropertyDevice } from "@/api/model/property-device";
import type { WaterPump } from "@/api/model/water-pump";
import { appConfig } from "@/utils/config";
import type { Account } from "./model/account";
import type { AccountUser } from "./model/account-user";
import type { CropType } from "./model/crop-type";
import type { CurrentIrrigationPlanState } from "./model/current-irrigation-plan-state";
import type { CurrentLICPacket } from "./model/current-lic-packet";
import type { CurrentProjectState } from "./model/current-project-state";
import type { CurrentReservoirState } from "./model/current-reservoir-state";
import type { Device } from "./model/device";
import type { DeviceMessageRequest } from "./model/device-message-request";
import type { IrrigationPlan } from "./model/irrigation-plan";
import type { IrrigationPlanSectorMirror } from "./model/irrigation-plan-sector-mirror";
import type { IrrigationPlanStep } from "./model/irrigation-plan-step";
import type { IrrigationPlanStepTank } from "./model/irrigation-plan-step-tank";
import type { LicState } from "./model/lic-state";
import type { MeshDeviceMapping } from "./model/mesh-device-mapping";
import type { Nutrient } from "./model/nutrient";
import type { Project } from "./model/project";
import type { ProjectTank } from "./model/project-tank";
import type { Property } from "./model/property";
import type { PropertyDeviceSensorAssignment } from "./model/property-device-sensor-assignment";
import type { Reservoir } from "./model/reservoir";
import type { ReservoirState } from "./model/reservoir-state";
import type { Sector } from "./model/sector";
import type { SectorCrop } from "./model/sector-crop";
import type { SensorModel } from "./model/sensor-model";
import type { Tank } from "./model/tank";
import type { User } from "./model/user";

// Aggregate schema from all model types
export interface AppSchema {
  directus_users: User[];
  account: Account[];
  account_user: AccountUser[];
  crop_type: CropType[];
  device: Device[];
  device_model: DeviceModel[];
  device_message_request: DeviceMessageRequest[];
  irrigation_plan: IrrigationPlan[];
  irrigation_plan_step_tank: IrrigationPlanStepTank[];
  mesh_device_mapping: MeshDeviceMapping[];
  nutrient: Nutrient[];
  project: Project[];
  project_tank: ProjectTank[];
  property: Property[];
  property_device: PropertyDevice[];
  property_device_sensor_assignment: PropertyDeviceSensorAssignment[];
  sensor_model: SensorModel[];
  irrigation_plan_step: IrrigationPlanStep[];
  irrigation_plan_sector_mirror: IrrigationPlanSectorMirror[];
  reservoir: Reservoir[];
  sector: Sector[];
  sector_crop: SectorCrop[];
  tank: Tank[];
  water_pump: WaterPump[];
  current_lic_packet: CurrentLICPacket[];
  current_project_state: CurrentProjectState[];
  current_irrigation_plan_state: CurrentIrrigationPlanState[];
  current_reservoir_state: CurrentReservoirState[];
  reservoir_state: ReservoirState[];
  lic_state: LicState[];
  virtual_device_configuration: VirtualDeviceConfiguration[];
}

// Custom localStorage adapter for Directus SDK authentication
class LocalStorage {
  get() {
    const data = localStorage.getItem("directus-auth-data");
    return data ? JSON.parse(data) : null;
  }
  set(value: unknown) {
    if (value === null) {
      localStorage.removeItem("directus-auth-data");
    } else {
      localStorage.setItem("directus-auth-data", JSON.stringify(value));
    }
  }
}

const apiURL = appConfig.api.baseUrl;

console.log("Directus API URL:", apiURL); // For debugging purposes

export type AppDirectusClient = ReturnType<typeof createDirectusInstance>;

export function createDirectusInstance(url = apiURL) {
  console.log("Creating Directus instance for", url);
  return createDirectus<AppSchema>(url)
    .with(rest())
    .with(authentication("json", { storage: new LocalStorage() }));
}
